import { Controller } from "@hotwired/stimulus";

// Handles inline rename of a contact-group chip.
// - click pencil -> startEdit: hides the label link, shows the form + delete
//                   button, focuses the input
// - Enter       -> form submits (PATCH), page reloads (turbo_frame "_top")
// - Esc / blur  -> cancel, restore the label view, hide the delete button,
//                  reset the input. Blur is guarded so the delete button
//                  stays clickable while the input is being exited.
export default class extends Controller {
  static targets = ["labelLink", "form", "input", "label", "deleteButton"];
  static values = { groupName: String, updateUrl: String };

  connect() {
    this._originalName = this.groupNameValue;
  }

  startEdit(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.hasFormTarget || !this.hasInputTarget) return;

    this.labelLinkTarget.classList.add("hidden");
    this.formTarget.classList.remove("hidden");
    this.formTarget.classList.add("inline-flex");
    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.classList.remove("hidden");
    }
    // Focus + select happens on next tick so the display swap lands first.
    requestAnimationFrame(() => {
      this.inputTarget.focus();
      this.inputTarget.select();
    });
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  onBlur(event) {
    // Blur cancels unless a submit is currently in-flight. Submission moves
    // focus off the input synchronously; onSubmit sets _submitting beforehand.
    if (this._submitting) return;

    // If focus is moving to another element inside this chip (the pencil, the
    // delete button, or any future action), stay in edit mode so the click
    // actually lands. We also pull focus back to the input so the usual
    // Esc/blur escape hatches keep working if the action is cancelled
    // (e.g. the delete turbo_confirm is dismissed).
    const next = event && event.relatedTarget;
    if (next && this.element.contains(next)) {
      requestAnimationFrame(() => {
        if (this.hasInputTarget) this.inputTarget.focus();
      });
      return;
    }

    this.cancelEdit();
  }

  onSubmit() {
    // Prevent the blur-triggered cancel from fighting the submit.
    this._submitting = true;
  }

  cancelEdit() {
    if (!this.hasFormTarget || !this.hasInputTarget) return;
    this.inputTarget.value = this._originalName;
    this.formTarget.classList.add("hidden");
    this.formTarget.classList.remove("inline-flex");
    this.labelLinkTarget.classList.remove("hidden");
    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.classList.add("hidden");
    }
  }
}
